import { useCallback, useMemo, useState } from "react";
import { ImagesVideosTabHeader } from "../controls/ImagesVideosTabHeader";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import {
  Checkbox,
  Collapse,
  ConfigProvider,
  Flex,
  Input,
  Space,
  TreeSelect,
  TreeSelectProps,
  Typography,
} from "antd";
import { SettingsLabel } from "../../../controls/SettingsLabel";
import { ScrollableMediaGallery } from "./ScrollableMediaGallery";
import {
  AspectRatioValue,
  Orientation,
  OrientationSelect,
} from "../controls/OrientationSelect";
import {
  AspectRatio,
  ImageGenerationEvent,
  MediaAsset,
  PaletteResponse,
} from "pages/media/types/";
import { SettingSlider } from "../../../controls/SettingSlider";
import { generateGenerationStylesTreeData } from "../../../../helpers/tabHelpers";
import { useTemplateData } from "../../../../hooks/useTemplateData";
import { useAntTheme } from "hooks/useAntTheme";
import styled from "styled-components";
import { CTAButton } from "../../CTAButton";
import { MediaKind } from "../../../../types";

export const AIGeneratedImages = ({
  previewWidth,
  previewHeight,
  assetLibraryId,
  projectId,
  storyboardId,
  palette,
  onReplaceSelectedAsset,
  customAddToCanvas,
  selectionMode,
}: {
  previewWidth: number;
  previewHeight: number;
  assetLibraryId: string;
  projectId: string;
  storyboardId: string;
  palette?: PaletteResponse;
  onReplaceSelectedAsset?: (
    assetId: string,
    path: string,
    mediaType: MediaKind
  ) => void;
  customAddToCanvas?: (assetId: string, path: string) => void;
  selectionMode?: boolean;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    AspectRatioValue.Square
  );
  const [useBrandkit, setUseBrandkit] = useState(false);
  const [totalImages, setTotalImages] = useState(2);
  const [userPrompt, setUserPrompt] = useState("");
  const [activeKey, setActiveKey] = useState<string | undefined>(
    "preGeneratedImages"
  );
  const [error, setError] = useState(false);
  const {
    projectMediaAssetsData: {
      images: projectImages,
      updateAllLoadedAssetsOptimistically,
      isLoading: isLoadingProjectMediaAssets,
      hasNextPage: hasMoreProjectAssets,
      fetchNextPage: fetchNextProjectPage,
    },
  } = useTemplateData();

  const handleFetchNextPage = useCallback(() => {
    fetchNextProjectPage().catch((error: unknown) =>
      console.error("Error fetching next project assets page", error)
    );
  }, [fetchNextProjectPage]);

  const { mutateAsync: generatePromptedImages, isPending } = useCustomMutation<{
    generated_images: MediaAsset[];
  }>();

  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);

  const preGeneratedImages = useMemo(() => {
    return projectImages.filter(
      (image) =>
        image.source_type === "Generated" &&
        (image.sub_category === "Section" || image.sub_category === "Slide")
    );
  }, [projectImages]);

  const promptGeneratedImages = useMemo(() => {
    return projectImages.filter(
      (image) =>
        image.source_type === "Generated" &&
        image.sub_category === "Custom Prompt"
    );
  }, [projectImages]);

  const [promptedImages, setPromptedImages] = useState<MediaAsset[]>([]);

  const imageTypes = useMemo(
    () => ({
      promptedImages,
      preGeneratedImages,
      promptGeneratedImages,
    }),
    [promptGeneratedImages, preGeneratedImages, promptedImages]
  );

  const handleGenerateImages = async () => {
    setError(false);
    if (!projectId || !storyboardId) return;
    const values: ImageGenerationEvent = {
      user_prompt: userPrompt,
      project_id: projectId,
      storyboard_id: storyboardId,
      aspect_ratio: aspectRatio,
      styles: selectedStyles,
      total_images: totalImages,
      brandkit_colors:
        useBrandkit && palette
          ? Object.values(palette)
              .map((value) => value)
              .filter((color) => color !== null)
          : [],
    };

    try {
      const { data } = await generatePromptedImages({
        url: "media/media/assets/image_generation",
        method: "post",
        values,
        errorNotification: false,
      });
      const generatedImages = data.generated_images;
      updateAllLoadedAssetsOptimistically(generatedImages);
      setPromptedImages(generatedImages);
    } catch (error) {
      console.error("Error in generatePromptedImages:", error);
      setError(true);
    }
  };

  const { data: stylesData } = useCustom<{ [key: string]: string[] }>({
    url: "/media/media/assets/image_generation/styles",
    method: "get",
  });

  const styles = stylesData?.data;

  const handleChange = (value: string[]) => {
    setSelectedStyles(value);
  };

  const treeData = generateGenerationStylesTreeData(styles);
  const treeProps: TreeSelectProps = {
    treeData,
    value: selectedStyles,
    onChange: handleChange,
    treeCheckable: false,
    placeholder: t(
      "components.templates.settings.imagesVideosTab.AIGeneratedImages.imageStyle"
    ),
    multiple: true,
    treeLine: true,
    treeDefaultExpandAll: false,
    maxTagCount: 2,
    allowClear: true,
  };

  const items = Object.keys(imageTypes)
    .filter((type): type is keyof typeof imageTypes => type in imageTypes)
    .filter((type) => type === "promptedImages" || !!imageTypes[type].length)
    .map((type) => {
      return {
        key: type,
        label: (
          <SettingsLabel
            type="secondary"
            style={{ textTransform: "uppercase" }}
          >
            {t(
              `components.templates.settings.imagesVideosTab.AIGeneratedImages.${type}.title`
            )}
          </SettingsLabel>
        ),
        children: (
          <CollapseContent vertical gap={10}>
            {type === "preGeneratedImages" && (
              <Typography.Text italic type="secondary">
                {t(
                  `components.templates.settings.imagesVideosTab.AIGeneratedImages.${type}.description`
                )}
              </Typography.Text>
            )}
            {type === "promptedImages" && (
              <Flex vertical gap={10}>
                <Input.TextArea
                  placeholder={t(
                    `components.templates.settings.imagesVideosTab.AIGeneratedImages.${type}.placeholder`
                  )}
                  autoSize={{
                    minRows: 3,
                    maxRows: 10,
                  }}
                  allowClear
                  onChange={(e) => {
                    setUserPrompt(e.target.value);
                  }}
                  style={{
                    backgroundColor: theme.colorBgLayout,
                    border: "none",
                  }}
                />
                <Flex gap={10}>
                  <ConfigProvider
                    theme={{
                      components: {
                        Select: {
                          borderRadius: 8,
                          colorBorder: theme.colorPrimary,
                          optionActiveBg: theme.colorPrimary,
                        },
                        TreeSelect: {
                          nodeHoverBg: theme.colorPrimary,
                        },
                      },
                    }}
                  >
                    <OrientationSelect
                      value={aspectRatio}
                      type="extended"
                      includeAll={false}
                      onChange={(value: Orientation) => {
                        if (value === "all") return;
                        setAspectRatio(value);
                      }}
                      styles={{
                        root: {
                          width: "50%",
                          maxWidth: 150,
                          borderRadius: 8,
                        },
                        popup: {
                          root: {
                            width: "fit-content",
                          },
                        },
                      }}
                    />
                    {styles && (
                      <TreeSelect
                        {...treeProps}
                        size="middle"
                        styles={{
                          popup: {
                            root: {
                              width: 250,
                            },
                          },
                          root: {
                            width: "50%",
                            maxWidth: 150,
                          },
                        }}
                      />
                    )}
                  </ConfigProvider>
                </Flex>
                <Space>
                  <SettingsLabel type="secondary" style={{ fontSize: 12 }}>
                    {t(
                      "components.templates.settings.imagesVideosTab.AIGeneratedImages.numberOfImages"
                    )}
                  </SettingsLabel>
                  <SettingSlider
                    value={totalImages}
                    onChange={(value: number) => setTotalImages(value)}
                    min={1}
                    max={3}
                  />
                </Space>
                <Checkbox
                  checked={useBrandkit}
                  onChange={(e) => {
                    setUseBrandkit(e.target.checked);
                  }}
                  disabled={!palette}
                >
                  {t(
                    "components.templates.settings.imagesVideosTab.AIGeneratedImages.useBrandkitColors"
                  )}
                </Checkbox>
                <CTAButton
                  loading={isPending}
                  disabled={userPrompt === "" || isPending}
                  onClick={() => {
                    handleGenerateImages().catch((e) => {
                      console.error("Error generating images", e);
                    });
                  }}
                >
                  {t(
                    `components.templates.settings.imagesVideosTab.AIGeneratedImages.${type}.cta`
                  )}
                </CTAButton>
                {error && (
                  <Typography.Text
                    type="danger"
                    style={{ textAlign: "center" }}
                  >
                    {t(
                      "components.templates.settings.imagesVideosTab.AIGeneratedImages.errorMessage"
                    )}
                  </Typography.Text>
                )}
              </Flex>
            )}
            {(!!imageTypes[type].length || hasMoreProjectAssets) && (
              <GalleryContainer id={`infinite-scroll-${type}`}>
                <ScrollableMediaGallery
                  currentAssetLibraryId={assetLibraryId}
                  previewWidth={previewWidth}
                  previewHeight={previewHeight}
                  assetsFromBrandkitAndLibrary={imageTypes[type]}
                  isLoading={isLoadingProjectMediaAssets}
                  scrollableTarget={`infinite-scroll-${type}`}
                  onNext={handleFetchNextPage}
                  hasMore={hasMoreProjectAssets}
                  onReplaceSelectedAsset={onReplaceSelectedAsset}
                  customAddToCanvas={customAddToCanvas}
                  selectionMode={selectionMode}
                />
              </GalleryContainer>
            )}
          </CollapseContent>
        ),
      };
    });

  return (
    <CollapseWrapper>
      <ImagesVideosTabHeader
        title={t(
          "components.templates.settings.imagesVideosTab.AIGeneratedImages.title"
        )}
      />
      <StyledCollapse
        items={items}
        ghost
        activeKey={activeKey}
        expandIconPosition="end"
        onChange={(keys: string[]) => {
          if (keys.length === 0) {
            setActiveKey(undefined);
            return;
          }
          if (!activeKey) {
            setActiveKey(keys[0]);
            return;
          } else {
            const newKey = keys.filter((key) => key !== activeKey)[0];
            setActiveKey(newKey);
          }
        }}
      />
    </CollapseWrapper>
  );
};

const CollapseWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const CollapseContent = styled(Flex)`
  flex: 1;
  overflow: hidden;
`;

const GalleryContainer = styled.div`
  flex: 1;
  overflow: auto;
  min-height: 0;
`;

const StyledCollapse = styled(Collapse)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-item {
    display: flex;
    flex-direction: column;

    .ant-collapse-header {
      padding-left: 0;
    }

    &.ant-collapse-item-active {
      flex: 1;
      overflow: hidden;

      .ant-collapse-content {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        .ant-collapse-content-box {
          flex: 1;
          overflow: hidden;
          padding: 0 !important;
          display: flex;
          flex-direction: column;
        }
      }
    }
  }
`;
